/**
 * SEO component — sets per-page <title>, meta description, canonical URL,
 * Open Graph & Twitter tags, plus optional JSON-LD structured data.
 *
 * Usage:
 *   <SEO
 *     title="BPC-157 — Research Peptide"
 *     description="High-purity BPC-157 for laboratory research use only."
 *     canonicalPath="/products/bpc-157"
 *     image="https://endag1.com/og/bpc-157.jpg"
 *     type="product"
 *     jsonLd={[{ "@context": "https://schema.org", ... }]}
 *   />
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://endag1.com";
const DEFAULT_IMAGE =
  "https://customer-assets.emergentagent.com/job_peptide-marketplace-8/artifacts/m2g3mnup_BPC-157%20PDC-1-%20White.png";

const SEO = ({
  title,
  description,
  canonicalPath = "",
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
  jsonLd = [],
  keywords,
}) => {
  const fullTitle = title
    ? `${title} — EndaG1`
    : "EndaG1 — Pharmaceutical Grade Peptides";
  const canonical = `${SITE_URL}${canonicalPath || "/"}`;
  const desc =
    description ||
    "EndaG1 — pharmaceutical-grade peptides manufactured to exacting standards, independently verified, and trusted by laboratories committed to scientific excellence. Research use only.";
  const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="EndaG1" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {schemas
        .filter(Boolean)
        .map((schema, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
    </Helmet>
  );
};

export default SEO;
